import {inject, ref, type InjectionKey} from 'vue';
import {defineStore} from 'pinia';
import {compareMessage, createChatApi, type ChatApiConfig} from './chatApi';
import {createTranscriptState} from './transcriptState';
import {vehicleTitle} from '../vehicle/vehicleFormat';
import type {ActiveFilter, ChatEntry, ChatResponse, Credentials, ReturnLine, Vehicle} from './chatTypes';
import type {CompareRequest} from '../../Store';

let nextStoreId = 0;

export function createChatStore(config: ChatApiConfig, initialVehicle: Vehicle | null = null) {
    // Pinia is shared by the site's Vue root. Give every mounted chat its own store so a
    // second chat on the page cannot inherit another conversation's transcript or API config.
    const useStore = defineStore(`chat:${config.conversationId}:${++nextStoreId}`, () => {
        const api = createChatApi(config);
        const transcript = createTranscriptState();
        const entries = ref<ChatEntry[]>([]);
        const pending = ref(false);
        const typing = ref(false);
        const activeFilters = ref<ActiveFilter[]>([]);
        const vehicle = ref<Vehicle | null>(initialVehicle);
        const lastTurnFirstEntryId = ref(0);

        function say(text: string, tone: 'me' | 'ai' | 'error'): void {
            transcript.append(entries.value, {
                kind: 'message',
                label: tone === 'me' ? 'Jij' : tone === 'error' ? 'Fout' : 'PartBot',
                text,
                tone,
            });
        }

        function apply(response: ChatResponse): void {
            transcript.apply(entries.value, response);
            activeFilters.value = response.activeFilters;
            if (!Array.isArray(response.vehicle)) vehicle.value = response.vehicle;
        }

        // A list-page comparison asked for while a turn or reset is still running. Unlike a message typed
        // into the busy composer, it was already taken from the compare bar, so dropping it would
        // lose the click: it runs as soon as the current operation is done.
        let queuedPageCompare: CompareRequest | null = null;

        async function drainPageCompare(): Promise<void> {
            if (queuedPageCompare === null) return;
            const next = queuedPageCompare;
            queuedPageCompare = null;
            await comparePageProducts(next);
        }

        async function request(run: () => Promise<ChatResponse>, displayMessage?: string): Promise<void> {
            if (pending.value) return;
            if (displayMessage) say(displayMessage, 'me');
            const disabled = transcript.disable(entries.value);
            const firstEntryId = transcript.nextId();
            lastTurnFirstEntryId.value = firstEntryId;
            pending.value = true;
            typing.value = true;
            try {
                apply(await run());
            } catch (error) {
                transcript.restore(entries.value, disabled);
                say((error as Error).message, 'error');
            } finally {
                typing.value = false;
                pending.value = false;
            }
            await drainPageCompare();
        }

        const send = (message: string) => request(() => api.send(message), message);
        const choose = (choice: {label: string; choiceId: string | null}) =>
            request(() => api.send(choice.label, choice.choiceId), choice.label);
        const compare = (productIds: string[]) => request(() => api.compare(productIds), compareMessage(productIds));
        function comparePageProducts(compare: CompareRequest): Promise<void> {
            if (pending.value) {
                queuedPageCompare = compare;
                return Promise.resolve();
            }
            return request(() => api.comparePageProducts(compare), compareMessage(compare.productIds));
        }
        const removeFilter = (filter: ActiveFilter) => request(() => api.removeFilter(filter));
        const chooseVehicleCandidate = (candidate: Vehicle) => {
            const label = `Dit is mijn voertuig: ${vehicleTitle(candidate, 'dit voertuig')}`;
            return request(() => api.send(label, candidate.id), label);
        };
        const verifyReturn = (credentials: Credentials) => request(() => api.verifyReturn(credentials.orderId, credentials.emailAddress));
        const verifyOrderStatus = (credentials: Credentials) =>
            request(() => api.verifyOrderStatus(credentials.orderId, credentials.emailAddress));
        const submitReturn = (orderLines: ReturnLine[]) => request(() => api.submitReturn(orderLines));
        const cancelReturn = () => request(() => api.cancelReturn());

        async function reset(): Promise<boolean> {
            if (pending.value) return false;
            pending.value = true;
            try {
                const response = await api.reset();
                entries.value = [];
                activeFilters.value = [];
                lastTurnFirstEntryId.value = 0;
                vehicle.value = response.car;
                return true;
            } catch (error) {
                say((error as Error).message, 'error');
                return false;
            } finally {
                pending.value = false;
                // Not awaited: the reset is done now, and the caller (ChatApp clearing the draft)
                // shouldn't wait out the comparison's round trip.
                void drainPageCompare();
            }
        }

        return {
            entries,
            pending,
            typing,
            activeFilters,
            vehicle,
            lastTurnFirstEntryId,
            send,
            choose,
            compare,
            comparePageProducts,
            removeFilter,
            chooseVehicleCandidate,
            verifyReturn,
            verifyOrderStatus,
            submitReturn,
            cancelReturn,
            reset,
        };
    });

    return useStore();
}

export type ChatStore = ReturnType<typeof createChatStore>;
export const chatStoreKey: InjectionKey<ChatStore> = Symbol('chatStore');

export function useChatStore(): ChatStore {
    const store = inject(chatStoreKey);
    if (!store) throw new Error('Chat store is only available inside ChatApp.');
    return store;
}
